import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  MdOutlineHome,
  MdOutlineWorkOutline,
  MdOutlineStarBorder,
  MdOutlineCandlestickChart,
  MdOutlineLeaderboard,
  MdOutlineInfo,
  MdOutlineSwapHoriz,
  MdOutlineSettings,
  MdKeyboardArrowRight,
  MdOutlinePerson,
  MdOutlineKey,
} from "react-icons/md";
import { toast } from "react-toastify";
import SwitchLeagueSheet from "../../pages/Game/league/SwitchLeagueSheet";
import { copyText, shareLink } from "../../utils/shareLink";
import { leagueShareData, leagueShareUrl } from "../../utils/shareMessage";
import { ShareUpIcon } from "../../pages/Game/landing/icons";
import { leagueHasEnded } from "../../utils/leagueAssets";
import { competitionCopy } from "../../pages/Game/landing/leagueFormat";
import useNavRefresh from "../../utils/navRefresh";

// The left rail INSIDE a league — the league's own nav, in the place the app's
// nav normally sits. Once you're a member, this column controls the league: it
// carries where you can go (Home, Portfolio, Watchlist, Trade), how you're doing
// (Leaderboard, League details, Switch league), and — for the organiser — how it
// runs. Leaving is the card at the very bottom, pinned away from the rest.
//
// The navigation component itself is NOT redesigned: same row height, padding,
// radius, icon family and placement, and the same typography as the live main
// menu. Only the selected-row colour changes (see LeagueNavRow).
//
// Breakpoints split it the way the main menu does. Desktop shows everything.
// Mobile drops the first group: the bottom bar already carries Home, Portfolio,
// Watchlist and Trade for this league, and league Home puts the leaderboard up
// front as a card, so repeating them in the 3-dot menu would be noise.
//
// The icons are the SAME glyphs the app's own nav uses for these destinations
// (see data/menus.js) — briefcase for Portfolio, star for Watchlist, candles for
// Trade, bars for Leaderboard. A league's Portfolio is still a portfolio, so it
// should not be wearing a different symbol from the one outside.
//
// Sharing DOES live here now. It used to be only a share icon on the leaderboard
// screen, on the reasoning that the link is always the thing you're looking at —
// until the client pointed out the hole that leaves: "I just realized Public
// League organizers can't share their league... the organizer can only view the
// league through the League Details screen, which doesn't have a native Share
// feature." Once you are inside a league there was no way back to the landing
// page to share it, for the organiser or for anyone who had joined.

// Matches SectionLabel in Sidebar. Desktop only: the mobile menu is three rows,
// which is too few to want dividing up.
const SectionLabel = ({ children }) => (
  <p className="mb-1 mt-5 hidden px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary md:block">
    {children}
  </p>
);

// The same row as Sidebar's NavRow — same height, padding, radius, type scale and
// icon size — with ONE thing changed: what "selected" looks like.
//
// Main QUSHi marks the current row with a pale mint fill and a dark-teal accent
// on its right edge. In here it's a solid dark-teal fill with white contents and
// no accent bar. That difference is the point: it's the environmental cue that
// you're inside a league rather than the app, and it only works if everything
// else about the row is identical.
//
// Desktop only, as on the main menu: the mobile 3-dot menu shows every row
// neutral, because a menu you opened deliberately doesn't need to tell you where
// you already are.
const ROW_BASE =
  "relative my-0.5 flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-sm font-medium transition-colors";

const LeagueNavRow = ({ Icon, label, to, end, onNavigate, suppressActive = false }) => {
  // Same rule as the main app's menu: the row you are already on refreshes
  // rather than going dead. See utils/navRefresh.js.
  const onRowClick = useNavRefresh(onNavigate);
  return (
  <NavLink
    to={to}
    end={end}
    title={label}
    onClick={onRowClick(to)}
    className={({ isActive }) =>
      `${ROW_BASE} ${
        isActive && !suppressActive
          ? "text-secondary md:bg-[#004d4d] md:font-semibold md:text-white"
          : "text-secondary hover:bg-gray-50"
      }`
    }
  >
    <Icon size={22} className="shrink-0" />
    <span className="truncate">{label}</span>
  </NavLink>
  );
};

// Switch league opens a selector rather than navigating, so it can't be a
// NavLink — but it has to be indistinguishable from one.
//
// Client Rev 5: "When you click on 'Switch league' the modal appears, but the
// green highlight does not highlight the chosen 'Switch league'."
//
// It could not: a NavLink gets its selected state from the router, and this row
// never changes the route — it opens a sheet. So the row had no idea it was the
// active one, and the rail went on highlighting whatever screen was underneath
// (in the client's screenshot, "League settings"). `active` is that missing
// signal, and it reuses the NavLink branch's class string CHARACTER FOR
// CHARACTER so the two rows cannot drift apart.
// `suppressActive` on every OTHER row is the other half of this. The highlight
// means "where you are", and you can only be in one place: with the sheet open
// the rail would otherwise show TWO solid teal rows — the screen underneath and
// Switch league. The sheet sits on top, so it holds the highlight until it
// closes, and the screen's own row takes it straight back. (The client asked for
// Switch league to highlight; showing it in two places at once is ours to get
// right.)
// LeagueActionRow hands its icons `size={22}`, which every react-icons glyph in
// this menu understands. ShareUpIcon is an inline SVG that takes its dimensions
// from className alone and would ignore `size` — rendering at the browser's
// default SVG box instead of 22px and throwing the row out of line with Switch
// league and Invite code. This adapter gives it the same box the others get.
const ShareRowIcon = ({ className = "" }) => (
  <ShareUpIcon className={`h-[22px] w-[22px] ${className}`} />
);

const LeagueActionRow = ({ Icon, label, onClick, active = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={active}
    className={`${ROW_BASE} w-full text-left ${
      active
        ? "text-secondary md:bg-[#004d4d] md:font-semibold md:text-white"
        : "text-secondary hover:bg-gray-50"
    }`}
  >
    <Icon size={22} className="shrink-0" />
    <span className="truncate">{label}</span>
  </button>
);

/**
 * The way out of the league environment. Pinned at the bottom of the rail by
 * Sidebar, below the account row — the same chrome as that row, because it's the
 * same kind of thing: not a place in this league, but the door.
 */
export const BackToQushiCard = ({ onNavigate }) => {
  const navigate = useNavigate();
  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;
  const [avatarSrc, setAvatarSrc] = useState(
    () => localStorage.getItem("img") || userInfo?.image || ""
  );

  return (
    <button
      type="button"
      onClick={() => {
        // The main dashboard, which is what this card says it does — not the
        // user's profile record. Client Rev 4 carried over: "Inside league
        // screens, the profile picture is currently an active link to the user's
        // main QUSHi profile record." This card is the deliberate way OUT of the
        // league, so it stays, but wearing the member's avatar it read as "my
        // profile" and behaved like it too.
        navigate("/feed");
        onNavigate?.();
      }}
      className="flex w-full items-center gap-3 rounded-2xl bg-[#F6F8F9] px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
    >
      {avatarSrc ? (
        <img
          src={avatarSrc}
          alt=""
          onError={() => setAvatarSrc("")}
          className="h-9 w-9 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500">
          <MdOutlinePerson size={22} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-gray-800">
          Back to QUSHi
        </span>
        {/* Client Rev 3, desktop: "Change the subtitle lines. The current one
            doesn't fit." — "Return to your main dashboard" was truncating to
            "Return to your ma…" in the 256px rail. This is the client's own
            replacement copy, and it fits. */}
        <span className="block truncate text-xs text-secondary">
          Leave League Play
        </span>
      </span>
      <MdKeyboardArrowRight size={22} className="shrink-0 text-gray-400" />
    </button>
  );
};

const LeagueMenu = ({ game, isOrganizer, onNavigate }) => {
  // Client 0014889 renamed this column's labels for a Cash Game. One map, so
  // the sidebar and the right rail cannot disagree about what to call the
  // competition. "Switch competition" is deliberately NOT from the map — see
  // its row below.
  const words = competitionCopy(game);
  // The organiser's invite code, and the row's own "Copied." confirmation.
  // The timer is cleared on unmount so a copy made just before leaving the
  // league cannot set state on a gone component.
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef(null);
  // The share row's own confirmation, kept separate from the invite code's so
  // one cannot reset the other.
  const [shareCopied, setShareCopied] = useState(false);
  const shareResetRef = useRef(null);
  // Private only. The client asked for "the PRIVATE league invite code… A private
  // organizer needs a way to copy it again" — and the code alone is not a good
  // enough test, because the backend issues one to public leagues too (checked in
  // the database: a public league carries e.g. TESTIN64). A public league has no
  // gate for a code to open, so surfacing it there would offer the organiser a
  // credential that does nothing.
  const isPrivateLeague = game?.game_type === "private";
  const inviteCode = isPrivateLeague ? game?.invite_code || "" : "";

  useEffect(
    () => () => {
      clearTimeout(copyResetRef.current);
      clearTimeout(shareResetRef.current);
    },
    []
  );

  const handleCopyInvite = async () => {
    const ok = await copyText(inviteCode);
    if (!ok) return;
    setCopied(true);
    clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setCopied(false), 2000);
  };

  /**
   * Share this league.
   *
   * Client: "Add 'Share League' to the organizer's three-dot menu (mobile) and
   * left menu bar (desktop)", and for a public league "the same 'Share League'
   * in the three-dot and left menu under MANAGE".
   *
   * Neither the wording nor the address is decided here — utils/shareMessage
   * owns both, so this row, the landing card's icon and the League Details
   * button all send the identical message. That is the client's requirement on
   * the native-sharing task: "all three share places… use the same setup for
   * image and text copy."
   *
   * shareLink() already handles every platform: the Flutter shell's own sheet
   * inside the app, navigator.share on mobile web, and a clipboard copy on
   * desktop. Only the desktop copy needs anything said about it — the OS sheet
   * is its own confirmation.
   */
  const handleShare = async () => {
    const result = await shareLink(leagueShareData(game, leagueShareUrl(game)));

    // Client Rev 5: "Share league should work the same way as Invite code.
    // Copies the landing page URL and briefly changes to 'League link copied' -
    // It then returns to Share league."
    //
    // So the row says it itself, exactly as the Invite code row does, rather
    // than throwing a toast across the screen. "shared" is deliberately silent:
    // that is the mobile path, where the OS sheet already confirmed the action —
    // "Share league opens the native share sheet - no change; this works, so
    // keep it as is."
    if (result === "copied") {
      setShareCopied(true);
      clearTimeout(shareResetRef.current);
      shareResetRef.current = setTimeout(() => setShareCopied(false), 2000);
    } else if (result === "failed") {
      toast.error("Could not copy the link");
    }
  };

  const [switchOpen, setSwitchOpen] = useState(false);
  const slug = game?.vanity_url;
  const leagueOver = leagueHasEnded(game);

  return (
    <>
      <nav className="sidebarNav px-4 pb-2 pt-1">
        {/* Where you can go in this league. Desktop only — the bottom bar owns
            these four on a phone. */}
        <div className="hidden md:block">
          <SectionLabel>{words.kind}</SectionLabel>
          <LeagueNavRow
            suppressActive={switchOpen}
            Icon={MdOutlineHome}
            label="Home"
            to={`/contest/${slug}`}
            end
            onNavigate={onNavigate}
          />
          <LeagueNavRow
            suppressActive={switchOpen}
            Icon={MdOutlineWorkOutline}
            label="Portfolio"
            to={`/contest/${slug}/portfolio`}
            onNavigate={onNavigate}
          />
          <LeagueNavRow
            suppressActive={switchOpen}
            Icon={MdOutlineStarBorder}
            label="Watchlist"
            to={`/${slug}/watchlist`}
            onNavigate={onNavigate}
          />
          {/* Client, "Completed League Member Access": "Disable trading and new
              orders inside a completed league." The order path itself is closed
              on both sides (utils/leagueAssets.leagueTradeBlockReason and
              App\Support\LeagueAssets::hasEnded), so this row would only lead
              somewhere that refuses; a nav entry that cannot do its job reads as
              a fault rather than a rule.

              Home, Portfolio and Watchlist all stay, and so does the league feed
              — "Keep the league feed available to members after completion."
              Only the one thing the client named is taken away. */}
          {!leagueOver && (
            <LeagueNavRow
              suppressActive={switchOpen}
              Icon={MdOutlineCandlestickChart}
              label="Trade"
              to={`/${slug}/trade`}
              onNavigate={onNavigate}
            />
          )}
        </div>

        <SectionLabel>Compete</SectionLabel>
        {/* Leaderboard is desktop-only here too: league Home leads with it as a
            card on mobile. */}
        <div className="hidden md:block">
          <LeagueNavRow
            suppressActive={switchOpen}
            Icon={MdOutlineLeaderboard}
            label="Leaderboard"
            to={`/${slug}/leaderboard`}
            onNavigate={onNavigate}
          />
        </div>
        <LeagueNavRow
          suppressActive={switchOpen}
          Icon={MdOutlineInfo}
          label={words.details}
          to={`/contest/${slug}/details`}
          onNavigate={onNavigate}
        />
        <LeagueActionRow
          Icon={MdOutlineSwapHoriz}
          label="Switch competition"
          onClick={() => setSwitchOpen(true)}
          active={switchOpen}
        />

        {/* Organiser only — a member has nothing to configure.

            ORDER IS THE CLIENT'S, and it is not the order this block had:
            "Please make this the order in the organizer's three-dot and desktop
            left column menus: Share League / Invite Code / League Settings."

            That reverses what they asked for earlier ("Under MANAGE below League
            settings 'Invite code'"), and the new instruction wins — sharing and
            the code are what an organiser reaches for while the league is
            running, settings is the rare one. */}
        {isOrganizer && (
          <>
            <SectionLabel>Manage</SectionLabel>
            {/* Client: "Add 'Share League' to the organizer's three-dot menu
                (mobile) and left menu bar (desktop)" — and for public leagues,
                "the same 'Share League' in the three-dot and left menu under
                MANAGE". One row for both kinds; what it SENDS differs by league
                type and that is decided in utils/shareMessage, not here.
                The glyph is the app's own share mark, the same one the landing
                card and the header use. */}
            {/* Client Rev 5: "'Share league' link needs a lowercase 'l' in the
                desktop left menu column and the three-dot menu on mobile." */}
            <LeagueActionRow
              Icon={ShareRowIcon}
              label={shareCopied ? "League link copied" : "Share league"}
              onClick={handleShare}
            />
            {/* Client Rev: "We need to put the private league invite code in
                league settings. A private organizer needs a way to copy it again
                besides just the payoff screen. A dynamic link that says 'Invite
                code'; you click it, and it says 'Copied.' Only for organizers."
                The label itself is the confirmation — it becomes "Copied" and
                returns on its own — because the client asked for the click to
                say so, and a row has nowhere else to put a message. Client Rev
                5: "remove the period after Copied".
                Gated on the code existing rather than on the league type: only a
                private league is issued one (see inviteCode.js), so a public
                league simply never renders the row. */}
            {inviteCode && (
              <LeagueActionRow
                Icon={MdOutlineKey}
                label={copied ? "Copied" : "Invite code"}
                onClick={handleCopyInvite}
              />
            )}
            <LeagueNavRow
              suppressActive={switchOpen}
              Icon={MdOutlineSettings}
              label={words.settings}
              to={`/contest/${slug}/settings`}
              onNavigate={onNavigate}
            />
          </>
        )}
      </nav>

      <SwitchLeagueSheet
        open={switchOpen}
        currentSlug={slug}
        onClose={() => setSwitchOpen(false)}
        onSwitch={() => {
          setSwitchOpen(false);
          onNavigate?.();
        }}
      />
    </>
  );
};

export default LeagueMenu;
